// Loyalty helpers - pure functions only (no DOM, no Supabase) so both the
// controller and any future popup/test code can import them.

// Input sanitizers shared by the panel form and the checkout fields.
// Phone fields accept digits ONLY; name fields accept letters (any language,
// incl. accents) plus spaces, hyphens, apostrophes, and periods - no digits
// or other symbols.
export const stripNonDigits = (s) => String(s ?? "").replace(/\D/g, "");
export const stripNonNameChars = (s) =>
  String(s ?? "").replace(/[^\p{L}\p{M}\s'.-]/gu, "");
// Email fields: the practical address character set (letters, digits, @ . _ % + -).
export const stripNonEmailChars = (s) =>
  String(s ?? "").replace(/[^A-Za-z0-9@._%+-]/g, "");

// Normalize a phone number to digits only. Returns null when the result is not
// a plausible phone (7-15 digits, per E.164 max length).
export const normalizePhone = function (raw) {
  const digits = String(raw ?? "").replace(/\D/g, "");
  if (digits.length < 7 || digits.length > 15) return null;
  return digits;
};

// Display formatting for a normalized phone. US-style 10-digit numbers get
// (555) 123-4567; 11 digits starting with 1 get +1 (555) 123-4567; anything
// else is shown as-is (international formats vary too much to guess).
export const formatPhone = function (digits) {
  const d = String(digits ?? "");
  if (d.length === 10) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  if (d.length === 11 && d.startsWith("1"))
    return `+1 (${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
  return d;
};

// "YYYY-MM-DD" for an ISO timestamp in the given IANA timezone. The sv-SE
// locale formats dates as YYYY-MM-DD natively - same technique as the
// controller's _dayBoundsInTz.
export const localDateKey = function (isoTs, tz) {
  return new Intl.DateTimeFormat("sv-SE", { timeZone: tz }).format(new Date(isoTs));
};

// ISO-week key "YYYY-Www" (Monday-based, matching Reports' Mon-Sun weeks) for a
// "YYYY-MM-DD" date key. Uses the standard ISO-8601 algorithm: the week that
// contains the year's first Thursday is week 1.
export const weekKey = function (dateKey) {
  const [y, m, d] = dateKey.split("-").map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  const dayNum = date.getUTCDay() || 7; // Mon=1 ... Sun=7
  date.setUTCDate(date.getUTCDate() + 4 - dayNum); // nearest Thursday
  const isoYear = date.getUTCFullYear();
  const yearStart = new Date(Date.UTC(isoYear, 0, 1));
  const week = Math.ceil(((date - yearStart) / 86400000 + 1) / 7);
  return `${isoYear}-W${String(week).padStart(2, "0")}`;
};

// Calendar-month key "YYYY-MM" for a "YYYY-MM-DD" date key.
export const monthKey = function (dateKey) {
  return dateKey.slice(0, 7);
};

// Step a "YYYY-MM-DD" date key back one day (UTC math on the key itself, so
// timezone conversion happens exactly once, in localDateKey).
const _prevDayKey = function (dateKey) {
  const [y, m, d] = dateKey.split("-").map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  date.setUTCDate(date.getUTCDate() - 1);
  return date.toISOString().slice(0, 10);
};

// Step an ISO-week key back one week.
const _prevWeekKey = function (wk) {
  const [y, w] = wk.split("-W").map(Number);
  // Convert back to a date inside that week (Thursday of week w) and subtract 7 days.
  const jan4 = new Date(Date.UTC(y, 0, 4));
  const jan4Day = jan4.getUTCDay() || 7;
  const week1Monday = new Date(jan4);
  week1Monday.setUTCDate(jan4.getUTCDate() - jan4Day + 1);
  const thursday = new Date(week1Monday);
  thursday.setUTCDate(week1Monday.getUTCDate() + (w - 1) * 7 + 3);
  thursday.setUTCDate(thursday.getUTCDate() - 7);
  return weekKey(thursday.toISOString().slice(0, 10));
};

// Step a "YYYY-MM" month key back one month.
const _prevMonthKey = function (mk) {
  const [y, m] = mk.split("-").map(Number);
  const prevY = m === 1 ? y - 1 : y;
  const prevM = m === 1 ? 12 : m - 1;
  return `${prevY}-${String(prevM).padStart(2, "0")}`;
};

// Compute a customer's purchase streak from their sale timestamps.
//   saleDates: array of ISO timestamps (non-voided sales)
//   unit: 'day' | 'week' | 'month'
//   tz: IANA timezone (business-local)
//   now: Date (injectable for tests; defaults to new Date())
// Returns { current, best, lastKey, dateKeys } where dateKeys is the deduped,
// sorted list of local "YYYY-MM-DD" purchase dates (newest first) for history UIs.
// A streak is ALIVE if the last purchase unit is the current unit OR the one
// immediately before it (buying yesterday still shows the streak today; it only
// breaks after a full missed unit).
export const computeStreak = function (saleDates, unit = "day", tz, now = new Date()) {
  const timeZone = tz || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const dayKeys = [...new Set((saleDates ?? []).map((ts) => localDateKey(ts, timeZone)))]
    .sort()
    .reverse();
  if (dayKeys.length === 0) return { current: 0, best: 0, lastKey: null, dateKeys: [] };

  const toUnit = unit === "week" ? weekKey : unit === "month" ? monthKey : (k) => k;
  const prevUnit = unit === "week" ? _prevWeekKey : unit === "month" ? _prevMonthKey : _prevDayKey;
  const unitKeys = [...new Set(dayKeys.map(toUnit))]; // newest first (dayKeys already sorted)

  // Walk all runs of consecutive units to find the best streak, tracking the
  // newest run separately.
  let best = 1;
  let run = 1;
  let newestRun = 1;
  for (let i = 1; i < unitKeys.length; i++) {
    if (unitKeys[i] === prevUnit(unitKeys[i - 1])) {
      run += 1;
    } else {
      run = 1;
    }
    if (run > best) best = run;
    if (run === i + 1) newestRun = run; // still contiguous from the newest unit
  }

  const nowUnit = toUnit(localDateKey(now.toISOString(), timeZone));
  const alive = unitKeys[0] === nowUnit || unitKeys[0] === prevUnit(nowUnit);
  return {
    current: alive ? newestRun : 0,
    best,
    lastKey: unitKeys[0],
    dateKeys: dayKeys,
  };
};

// All three streak units at once (the customer detail shows daily, weekly,
// and monthly side by side - there is no business-level streak-unit setting).
export const computeAllStreaks = function (saleDates, tz, now = new Date()) {
  return {
    day: computeStreak(saleDates, "day", tz, now),
    week: computeStreak(saleDates, "week", tz, now),
    month: computeStreak(saleDates, "month", tz, now),
  };
};

const MONTH_NAMES = ["January","February","March","April","May","June","July",
  "August","September","October","November","December"];

// "March 4" for a stored birthday_month/birthday_day pair, or null when
// either half is missing (a lone month or day is never stored - see
// enrollCustomer/_customerExtras).
export const formatBirthday = function (month, day) {
  if (!month || !day) return null;
  return `${MONTH_NAMES[month - 1]} ${day}`;
};

// True when today (business-local) is the customer's birthday. Year-agnostic
// by design - birthdays are stored without a year.
export const isBirthdayToday = function (month, day, tz, now = new Date()) {
  if (!month || !day) return false;
  const timeZone = tz || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [, m, d] = localDateKey(now.toISOString(), timeZone).split("-").map(Number);
  return m === month && d === day;
};

// Cart-line ids currently comped via a free-item reward redemption, derived
// from a sale's (or the live checkout's) adjustments array. Lets receipts and
// the sale-detail modal show that item's own price as $0 instead of a
// separate "Free X (Reward)" discount line - same source data, no schema.
export const redeemedRewardLineIds = function (adjustments) {
  return new Set(
    (adjustments ?? [])
      .filter((a) => !a.removed && a.source === "loyalty_reward")
      .map((a) => a.cartItemLineId),
  );
};
